import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  memo,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useColors } from "../constants/Colors";
import { trpcReact } from "../trpc_client";
import { usePostHog } from "posthog-js/react";
import {
  GoogleMap,
  useJsApiLoader,
  OverlayViewF,
  OverlayView,
} from "@react-google-maps/api";
import type { inferRouterInputs } from "@trpc/server";
import { MAP_STYLES, GOOGLE_MAPS_API_KEY } from "../constants/MapStyles";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import AuthModal from "../components/AuthModal";
import {
  SlidersHorizontal,
  X,
  MapPin,
  Search,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { debounce } from "../utils/debounce";
import type { WebAppRouter } from "../../../NativeApp/Backend/main/src/index";

// Address suggestion type from Nominatim API
interface AddressSuggestion {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  type?: string;
  class?: string;
  address?: {
    country_code?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
}

// Mirror of backend enums
const FlatPreference = {
  TEMPORARY: "temporary",
  PERMANENT: "permanent",
  BOTH: "both",
} as const;

const ShowAppliedFlats = {
  ONLY_APPLIED: "Applied",
  SHOW_ALL: "Show all",
  EXCLUDE_APPLIED: "Not applied",
} as const;

// Default location (All Switzerland)
const DEFAULT_LOCATION = {
  name: "Switzerland",
  lat: 46.8182,
  lon: 8.2275,
  delta: 2.5,
};

// Session storage keys for persisting filters and scroll position
const FILTERS_STORAGE_KEY = "flateroo_browse_filters";
const SCROLL_POSITION_KEY = "flateroo_browse_scroll";

type WebAppRouterInputs = inferRouterInputs<WebAppRouter>;
type RoomsFilterInput = WebAppRouterInputs["getRooms"]["filter"];
type RoomsQueryInputObject = WebAppRouterInputs["getRooms"];

const FLAT_PREFERENCE_VALUES = [
  FlatPreference.TEMPORARY,
  FlatPreference.PERMANENT,
  FlatPreference.BOTH,
] as const;
const SHOW_APPLIED_VALUES = [
  ShowAppliedFlats.ONLY_APPLIED,
  ShowAppliedFlats.SHOW_ALL,
  ShowAppliedFlats.EXCLUDE_APPLIED,
] as const;

interface PersistedBrowseFilters {
  priceRange: { min: number; max: number };
  flatmateCount: number;
  minRoomSize: number;
  genderFilter: boolean;
  stayType: RoomsFilterInput["permanent"];
  appliedFilter: RoomsFilterInput["show_applied_flats"];
  selectedLocation: typeof DEFAULT_LOCATION;
  searchInput: string;
  mapRegion: { lat: number; lon: number; delta: number };
}

const isFlatPreference = (
  value: string,
): value is RoomsFilterInput["permanent"] =>
  FLAT_PREFERENCE_VALUES.includes(
    value as (typeof FLAT_PREFERENCE_VALUES)[number],
  );

const isShowAppliedFlats = (
  value: string,
): value is RoomsFilterInput["show_applied_flats"] =>
  SHOW_APPLIED_VALUES.includes(value as (typeof SHOW_APPLIED_VALUES)[number]);

// Helper to load filters from sessionStorage
const loadPersistedFilters = (): PersistedBrowseFilters | null => {
  try {
    const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<PersistedBrowseFilters>;
      const stayType = parsed.stayType;
      const appliedFilter = parsed.appliedFilter;
      if (
        !stayType ||
        !appliedFilter ||
        !isFlatPreference(stayType) ||
        !isShowAppliedFlats(appliedFilter)
      ) {
        return null;
      }

      return {
        priceRange: parsed.priceRange ?? { min: 0, max: 10000 },
        flatmateCount: parsed.flatmateCount ?? 20,
        minRoomSize: parsed.minRoomSize ?? 0,
        genderFilter: parsed.genderFilter ?? false,
        stayType,
        appliedFilter,
        selectedLocation: parsed.selectedLocation ?? DEFAULT_LOCATION,
        searchInput: parsed.searchInput ?? "",
        mapRegion: parsed.mapRegion ?? {
          lat: DEFAULT_LOCATION.lat,
          lon: DEFAULT_LOCATION.lon,
          delta: DEFAULT_LOCATION.delta,
        },
      };
    }
  } catch (e) {
    console.error("Error loading persisted filters:", e);
  }
  return null;
};

// Helper to save filters to sessionStorage
const saveFiltersToStorage = (filters: PersistedBrowseFilters) => {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.error("Error saving filters to storage:", e);
  }
};

// Filter modal – extracted so draft state changes don't re-render the listing grid
interface FilterValues {
  priceRange: { min: number; max: number };
  flatmateCount: number;
  minRoomSize: number;
  genderFilter: boolean;
  stayType: RoomsFilterInput["permanent"];
  appliedFilter: RoomsFilterInput["show_applied_flats"];
}

const FilterModal: React.FC<{
  filters: FilterValues;
  showGenderFilter: boolean;
  onApply: (filters: FilterValues) => void;
  onClose: () => void;
}> = ({ filters, showGenderFilter, onApply, onClose }) => {
  const { t } = useTranslation();
  const {
    containerNeutralSolidSurface,
    containerNeutralLightMedium,
    containerNeutralLightLowest,
    accentColor,
    textNeutralDefault,
    textNeutralMuted,
    textNeutralOnPrimary,
  } = useColors();

  const [draftPriceRange, setDraftPriceRange] = useState(filters.priceRange);
  const [draftFlatmateCount, setDraftFlatmateCount] = useState(
    filters.flatmateCount,
  );
  const [draftMinRoomSize, setDraftMinRoomSize] = useState(filters.minRoomSize);
  const [draftGenderFilter, setDraftGenderFilter] = useState(
    filters.genderFilter,
  );
  const [draftStayType, setDraftStayType] = useState(filters.stayType);
  const [draftAppliedFilter, setDraftAppliedFilter] = useState(
    filters.appliedFilter,
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 animate-[fadeIn_200ms_ease-out]"
        style={{
          backgroundColor: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full sm:max-w-md sm:mx-4 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[85vh] sm:max-h-[90vh] flex flex-col animate-[slideUp_300ms_cubic-bezier(0.16,1,0.3,1)] sm:animate-[scaleIn_200ms_cubic-bezier(0.16,1,0.3,1)]"
        style={{ backgroundColor: containerNeutralSolidSurface }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <h2
            className="text-lg font-bold tracking-tight"
            style={{ color: textNeutralDefault }}
          >
            {t("filters") || "Filters"}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:scale-105 active:scale-95"
            style={{ backgroundColor: containerNeutralLightLowest }}
          >
            <X size={18} style={{ color: textNeutralDefault }} />
          </button>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <div className="space-y-5">
            {/* Price Range */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
                style={{ color: textNeutralMuted }}
              >
                {t("price_range") || "Price range"} (CHF)
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="number"
                    value={draftPriceRange.min || ""}
                    onChange={(e) =>
                      setDraftPriceRange((prev) => ({
                        ...prev,
                        min: parseInt(e.target.value) || 0,
                      }))
                    }
                    placeholder={t("min") || "Min"}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                    style={
                      {
                        backgroundColor: containerNeutralLightLowest,
                        color: textNeutralDefault,
                        focusRingColor: accentColor,
                      } as React.CSSProperties
                    }
                  />
                </div>
                <div
                  className="w-5 h-px flex-shrink-0"
                  style={{ backgroundColor: containerNeutralLightMedium }}
                />
                <div className="flex-1 relative">
                  <input
                    type="number"
                    value={
                      draftPriceRange.max === 10000 ? "" : draftPriceRange.max
                    }
                    onChange={(e) =>
                      setDraftPriceRange((prev) => ({
                        ...prev,
                        max: parseInt(e.target.value) || 10000,
                      }))
                    }
                    placeholder={t("max") || "Max"}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                    style={{
                      backgroundColor: containerNeutralLightLowest,
                      color: textNeutralDefault,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div
              className="h-px"
              style={{ backgroundColor: containerNeutralLightLowest }}
            />

            {/* Number of Flatmates */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
                style={{ color: textNeutralMuted }}
              >
                {t("max_flatmates") || "Max. flatmates"}
              </label>
              <div className="flex flex-wrap gap-2">
                {[2, 3, 4, 5, 6, 20].map((count) => (
                  <button
                    key={count}
                    onClick={() => setDraftFlatmateCount(count)}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor:
                        draftFlatmateCount === count
                          ? accentColor
                          : containerNeutralLightLowest,
                      color:
                        draftFlatmateCount === count
                          ? textNeutralOnPrimary
                          : textNeutralDefault,
                      boxShadow:
                        draftFlatmateCount === count
                          ? `0 2px 8px ${accentColor}40`
                          : "none",
                    }}
                  >
                    {count === 20 ? t("any") || "Any" : count}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div
              className="h-px"
              style={{ backgroundColor: containerNeutralLightLowest }}
            />

            {/* Minimum Room Size */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
                style={{ color: textNeutralMuted }}
              >
                {t("min_room_size") || "Min. room size"} (m²)
              </label>
              <div className="flex flex-wrap gap-2">
                {[0, 8, 10, 12, 15, 20].map((size) => (
                  <button
                    key={size}
                    onClick={() => setDraftMinRoomSize(size)}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor:
                        draftMinRoomSize === size
                          ? accentColor
                          : containerNeutralLightLowest,
                      color:
                        draftMinRoomSize === size
                          ? textNeutralOnPrimary
                          : textNeutralDefault,
                      boxShadow:
                        draftMinRoomSize === size
                          ? `0 2px 8px ${accentColor}40`
                          : "none",
                    }}
                  >
                    {size === 0 ? t("any") || "Any" : `${size}+`}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div
              className="h-px"
              style={{ backgroundColor: containerNeutralLightLowest }}
            />

            {/* Stay Type */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
                style={{ color: textNeutralMuted }}
              >
                {t("stay_type") || "Stay type"}
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: FlatPreference.BOTH, label: t("all") || "All" },
                  {
                    value: FlatPreference.PERMANENT,
                    label: t("permanent") || "Permanent",
                  },
                  {
                    value: FlatPreference.TEMPORARY,
                    label: t("temporary") || "Temporary",
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      setDraftStayType(
                        option.value as RoomsFilterInput["permanent"],
                      )
                    }
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor:
                        draftStayType === option.value
                          ? accentColor
                          : containerNeutralLightLowest,
                      color:
                        draftStayType === option.value
                          ? textNeutralOnPrimary
                          : textNeutralDefault,
                      boxShadow:
                        draftStayType === option.value
                          ? `0 2px 8px ${accentColor}40`
                          : "none",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div
              className="h-px"
              style={{ backgroundColor: containerNeutralLightLowest }}
            />

            {/* Application Status */}
            <div>
              <label
                className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
                style={{ color: textNeutralMuted }}
              >
                {t("application_status") || "Application status"}
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  {
                    value: ShowAppliedFlats.SHOW_ALL,
                    label: t("all") || "All",
                  },
                  {
                    value: ShowAppliedFlats.EXCLUDE_APPLIED,
                    label: t("not_applied") || "Not applied",
                  },
                  {
                    value: ShowAppliedFlats.ONLY_APPLIED,
                    label: t("applied") || "Applied",
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      setDraftAppliedFilter(
                        option.value as RoomsFilterInput["show_applied_flats"],
                      )
                    }
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor:
                        draftAppliedFilter === option.value
                          ? accentColor
                          : containerNeutralLightLowest,
                      color:
                        draftAppliedFilter === option.value
                          ? textNeutralOnPrimary
                          : textNeutralDefault,
                      boxShadow:
                        draftAppliedFilter === option.value
                          ? `0 2px 8px ${accentColor}40`
                          : "none",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Gender Filter */}
            {showGenderFilter && (
              <>
                <div
                  className="h-px"
                  style={{ backgroundColor: containerNeutralLightLowest }}
                />
                <div>
                  <label
                    className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
                    style={{ color: textNeutralMuted }}
                  >
                    {t("gender_match") || "Gender preference"}
                  </label>
                  <button
                    onClick={() => setDraftGenderFilter(!draftGenderFilter)}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all duration-150 active:scale-[0.98]"
                    style={{
                      backgroundColor: draftGenderFilter
                        ? `${accentColor}12`
                        : containerNeutralLightLowest,
                      border: draftGenderFilter
                        ? `1.5px solid ${accentColor}`
                        : `1.5px solid transparent`,
                    }}
                  >
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200"
                      style={{
                        backgroundColor: draftGenderFilter
                          ? accentColor
                          : "transparent",
                        border: draftGenderFilter
                          ? "none"
                          : `2px solid ${containerNeutralLightMedium}`,
                      }}
                    >
                      {draftGenderFilter && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <span
                      className="text-sm font-medium"
                      style={{ color: textNeutralDefault }}
                    >
                      {t("only_matching_gender") ||
                        "Only show flats looking for my gender"}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{
            borderTop: `1px solid ${containerNeutralLightLowest}`,
          }}
        >
          <button
            onClick={() => {
              setDraftPriceRange({ min: 0, max: 10000 });
              setDraftFlatmateCount(20);
              setDraftMinRoomSize(0);
              setDraftGenderFilter(false);
              setDraftStayType(
                FlatPreference.BOTH as RoomsFilterInput["permanent"],
              );
              setDraftAppliedFilter(
                ShowAppliedFlats.SHOW_ALL as RoomsFilterInput["show_applied_flats"],
              );
            }}
            className="px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-150 hover:scale-105 active:scale-95"
            style={{
              color: textNeutralMuted,
              backgroundColor: containerNeutralLightLowest,
            }}
          >
            {t("clear_all") || "Clear all"}
          </button>
          <button
            onClick={() =>
              onApply({
                priceRange: draftPriceRange,
                flatmateCount: draftFlatmateCount,
                minRoomSize: draftMinRoomSize,
                genderFilter: draftGenderFilter,
                stayType: draftStayType,
                appliedFilter: draftAppliedFilter,
              })
            }
            className="px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-150 hover:scale-105 hover:opacity-90 active:scale-95"
            style={{
              backgroundColor: accentColor,
              color: textNeutralOnPrimary,
              boxShadow: `0 4px 12px ${accentColor}30`,
            }}
          >
            {t("show_results") || "Show results"}
          </button>
        </div>
      </div>
    </div>
  );
};

const BrowseFlats: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { user } = useAuth();
  const { userExistsInDb } = useProfile();
  const {
    containerNeutralSolidSurface,
    containerNeutralLightMedium,
    containerNeutralLightLowest,
    containerTertiarySolidLow,
    accentColor,
    textNeutralDefault,
    textNeutralMuted,
    textNeutralOnPrimary,
    textTertiaryOnLow,
  } = useColors();

  // Load persisted filters on initial render
  const persistedFilters = useMemo(() => loadPersistedFilters(), []);

  // Location state
  const [selectedLocation, setSelectedLocation] = useState(
    persistedFilters?.selectedLocation || DEFAULT_LOCATION,
  );
  const [searchInput, setSearchInput] = useState(
    persistedFilters?.searchInput || "",
  );
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Filter states
  const [priceRange, setPriceRange] = useState(
    persistedFilters?.priceRange || { min: 0, max: 10000 },
  );
  const [flatmateCount, setFlatmateCount] = useState(
    persistedFilters?.flatmateCount ?? 20,
  ); // max
  const [minRoomSize, setMinRoomSize] = useState(
    persistedFilters?.minRoomSize ?? 0,
  );
  const [genderFilter, setGenderFilter] = useState(
    persistedFilters?.genderFilter ?? false,
  );
  const [stayType, setStayType] = useState<RoomsFilterInput["permanent"]>(
    persistedFilters?.stayType ||
      (FlatPreference.BOTH as RoomsFilterInput["permanent"]),
  );
  const [appliedFilter, setAppliedFilter] = useState<
    RoomsFilterInput["show_applied_flats"]
  >(
    persistedFilters?.appliedFilter ||
      (ShowAppliedFlats.SHOW_ALL as RoomsFilterInput["show_applied_flats"]),
  );

  // Filter modal state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Track the current map region for querying (can differ from selectedLocation when user pans)
  const [mapRegion, setMapRegion] = useState(
    persistedFilters?.mapRegion || {
      lat: DEFAULT_LOCATION.lat,
      lon: DEFAULT_LOCATION.lon,
      delta: DEFAULT_LOCATION.delta,
    },
  );

  // Persist filters whenever they change
  useEffect(() => {
    saveFiltersToStorage({
      priceRange,
      flatmateCount,
      minRoomSize,
      genderFilter,
      stayType,
      appliedFilter,
      selectedLocation,
      searchInput,
      mapRegion,
    });
  }, [
    priceRange,
    flatmateCount,
    minRoomSize,
    genderFilter,
    stayType,
    appliedFilter,
    selectedLocation,
    searchInput,
    mapRegion,
  ]);

  // Count active filters (excluding defaults)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (priceRange.min > 0) count++;
    if (priceRange.max < 10000) count++;
    if (flatmateCount < 20) count++;
    if (minRoomSize > 0) count++;
    if (genderFilter) count++;
    if (stayType !== FlatPreference.BOTH) count++;
    if (appliedFilter !== ShowAppliedFlats.SHOW_ALL) count++;
    return count;
  }, [
    priceRange,
    flatmateCount,
    minRoomSize,
    genderFilter,
    stayType,
    appliedFilter,
  ]);

  // Fetch location suggestions from Nominatim API
  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const europeBoundingBox = "-12.69,36.29,24.57,58.84";
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query,
        )}&viewbox=${europeBoundingBox}&bounded=1&addressdetails=1`,
        { headers: { "User-Agent": "flateroo/1.0 webapp" } },
      );
      const data: AddressSuggestion[] = await response.json();

      // Filter unique results
      const unique = data.filter(
        (s, i, arr) =>
          i === arr.findIndex((x) => x.display_name === s.display_name),
      );
      setSuggestions(unique.slice(0, 5));
      setShowSuggestions(true);
    } catch (e) {
      console.error("Error fetching location suggestions:", e);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedFetch = useMemo(
    () => debounce((query: string) => fetchSuggestions(query), 300),
    [fetchSuggestions],
  );

  // Handle search input change
  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (value.trim()) {
        debouncedFetch(value);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [debouncedFetch],
  );

  // Handle selecting a suggestion
  const handleSelectSuggestion = useCallback(
    (suggestion: AddressSuggestion) => {
      const lat = parseFloat(suggestion.lat);
      const lon = parseFloat(suggestion.lon);

      // Determine delta based on place type/class
      let delta = 0.3; // default for cities
      const placeType = suggestion.type || "";
      const placeClass = suggestion.class || "";

      if (placeClass === "boundary" && placeType === "administrative") {
        // Country or state level
        delta = suggestion.address?.country_code ? 1.0 : 2.5;
      } else if (
        placeType === "city" ||
        placeType === "town" ||
        placeType === "village"
      ) {
        delta = 0.3;
      } else if (
        placeType === "suburb" ||
        placeType === "neighbourhood" ||
        placeType === "quarter"
      ) {
        delta = 0.15;
      } else if (placeClass === "highway" || placeType === "house") {
        delta = 0.05;
      }

      const newLocation = {
        name: suggestion.display_name,
        lat,
        lon,
        delta,
      };

      setSelectedLocation(newLocation);
      setSearchInput(suggestion.display_name);
      setMapRegion({ lat, lon, delta });
      setSuggestions([]);
      setShowSuggestions(false);
    },
    [],
  );

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update map region when location changes
  useEffect(() => {
    setMapRegion({
      lat: selectedLocation.lat,
      lon: selectedLocation.lon,
      delta: selectedLocation.delta,
    });
  }, [selectedLocation]);

  useEffect(() => {
    document.body.style.backgroundColor = containerNeutralSolidSurface;
    document.documentElement.style.backgroundColor =
      containerNeutralSolidSurface;
    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [containerNeutralSolidSurface]);

  // Query filter parameters
  const filterParams: RoomsQueryInputObject = {
    filter: {
      min_price: priceRange.min,
      max_price: priceRange.max,
      member_count_limit: flatmateCount,
      min_room_size: minRoomSize,
      flats_looking_for_my_gender: genderFilter,
      permanent: stayType,
      show_applied_flats: appliedFilter,
      region: {
        latitude: mapRegion.lat,
        longitude: mapRegion.lon,
        latitudeDelta: mapRegion.delta,
        longitudeDelta: mapRegion.delta,
      },
    },
  };

  // Use authenticated endpoint when user exists in database
  const authenticatedFlatsQuery = trpcReact.getRooms.useQuery(filterParams, {
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: userExistsInDb,
  });

  // Use public endpoint when user is not logged in OR doesn't exist in database yet
  const publicFlatsQuery = trpcReact.publicGetRooms.useQuery(filterParams, {
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !userExistsInDb,
  });

  // Use data from the appropriate query based on database user existence
  const flatsQuery = userExistsInDb
    ? authenticatedFlatsQuery
    : publicFlatsQuery;

  type FlatsData = NonNullable<typeof flatsQuery.data>;
  const [lastSuccessfulFlats, setLastSuccessfulFlats] = useState<FlatsData>([]);

  useEffect(() => {
    if (flatsQuery.isSuccess && Array.isArray(flatsQuery.data)) {
      setLastSuccessfulFlats(flatsQuery.data);
    }
  }, [flatsQuery.isSuccess, flatsQuery.data]);

  const flats = useMemo(() => {
    // Only re-derive when lastSuccessfulFlats changes — not on every refetch.
    // lastSuccessfulFlats is only updated when the query succeeds, so the grid
    // stays stable while a new fetch is in-flight.
    return [...lastSuccessfulFlats].sort((a, b) => {
      const dateA = new Date(a.room.flat.created_at || 0).getTime();
      const dateB = new Date(b.room.flat.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [lastSuccessfulFlats]);

  // Save scroll position on scroll
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem(
        SCROLL_POSITION_KEY,
        scrollContainerRef.current.scrollTop.toString(),
      );
    }
  }, []);

  // Debounced scroll handler to avoid too many writes
  const debouncedScrollHandler = useMemo(
    () => debounce(handleScroll, 100),
    [handleScroll],
  );

  // Restore scroll position after flats are loaded
  useEffect(() => {
    if (
      !flatsQuery.isLoading &&
      flats.length > 0 &&
      scrollContainerRef.current
    ) {
      const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
      if (savedScrollPosition) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = parseInt(
              savedScrollPosition,
              10,
            );
          }
        });
      }
    }
  }, [flatsQuery.isLoading, flats.length]);

  const [showAuthModal, setShowAuthModal] = useState(false);

  const openPlayStore = () => {
    window.location.href =
      "https://play.google.com/store/apps/details?id=com.flateroo.flateroo";
  };
  const openAppStore = () => {
    window.location.href = "https://apps.apple.com/app/flateroo/id6683311358";
  };

  const openStore = () => {
    const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    posthog?.capture("browse_open_store", { isIphone, isAndroid });
    if (isIphone) {
      openAppStore();
    } else if (isAndroid) {
      openPlayStore();
    } else {
      // Desktop - show app store
      openAppStore();
    }
  };

  // Navigate to flat view with data for faster initial render
  const handleFlatNavigate = useCallback(
    (flatId: number, flatData: (typeof flats)[0]) => {
      navigate(`/app/flat-view/${flatId}`, { state: { flatData } });
    },
    [navigate],
  );

  // Image carousel component with native scroll-snap on mobile and sliding animation on desktop
  const ImageCarousel = ({
    images,
    altText,
  }: {
    images: string[];
    altText: string;
  }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const goToPrevious = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (currentIndex > 0) {
          setCurrentIndex((prev) => prev - 1);
        }
      },
      [currentIndex],
    );

    const goToNext = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (currentIndex < images.length - 1) {
          setCurrentIndex((prev) => prev + 1);
        }
      },
      [currentIndex, images.length],
    );

    // Handle scroll for mobile snap carousel
    const handleScroll = useCallback(() => {
      if (containerRef.current) {
        const { scrollLeft, clientWidth } = containerRef.current;
        const newIndex = Math.round(scrollLeft / clientWidth);
        setCurrentIndex(newIndex);
      }
    }, []);

    if (images.length === 0) {
      return (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ backgroundColor: containerNeutralLightMedium }}
        >
          <span style={{ color: textNeutralMuted }}>No image</span>
        </div>
      );
    }

    const isAtStart = currentIndex === 0;
    const isAtEnd = currentIndex === images.length - 1;

    return (
      <div className="relative w-full h-full overflow-hidden pointer-events-none md:pointer-events-none">
        {/* Mobile: Native scroll-snap carousel */}
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="md:hidden flex overflow-x-auto snap-x snap-mandatory h-full w-full scrollbar-hide pointer-events-auto"
        >
          {images.map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`${altText} ${index + 1}`}
              className="flex-shrink-0 w-full h-full object-cover snap-center pointer-events-none"
              draggable={false}
              loading={index === 0 ? "eager" : "lazy"}
              decoding={index === 0 ? "sync" : "async"}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
          ))}
        </div>

        {/* Desktop: Sliding carousel */}
        <div className="hidden md:block h-full w-full overflow-hidden">
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{
              width: `${images.length * 100}%`,
              transform: `translateX(-${
                (currentIndex * 100) / images.length
              }%)`,
            }}
          >
            {images.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`${altText} ${index + 1}`}
                className="h-full object-cover"
                style={{ width: `${100 / images.length}%` }}
                draggable={false}
                loading={index === 0 ? "eager" : "lazy"}
                decoding={index === 0 ? "sync" : "async"}
                fetchPriority={index === 0 ? "high" : "auto"}
              />
            ))}
          </div>
        </div>

        {images.length > 1 && (
          <>
            {/* Left arrow - only show when not at start (desktop only) */}
            {!isAtStart && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  goToPrevious(e);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-10 pointer-events-auto"
                style={{
                  backgroundColor: containerNeutralSolidSurface,
                  color: textNeutralDefault,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            )}

            {/* Right arrow - only show when not at end (desktop only) */}
            {!isAtEnd && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  goToNext(e);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-10 pointer-events-auto"
                style={{
                  backgroundColor: containerNeutralSolidSurface,
                  color: textNeutralDefault,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            )}

            {/* Dots indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none z-10">
              {images.map((_, index) => (
                <div
                  key={index}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor:
                      index === currentIndex
                        ? "white"
                        : "rgba(255, 255, 255, 0.5)",
                    transform:
                      index === currentIndex ? "scale(1.3)" : "scale(1)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Skeleton loader for cards
  const SkeletonCard = () => (
    <div className="animate-pulse">
      <div
        className="aspect-square w-full rounded-[24px]"
        style={{ backgroundColor: containerNeutralLightMedium }}
      />
      <div className="pt-4 pb-2 space-y-2">
        <div
          className="h-5 w-3/4 rounded"
          style={{ backgroundColor: containerNeutralLightMedium }}
        />
        <div
          className="h-4 w-1/2 rounded"
          style={{ backgroundColor: containerNeutralLightMedium }}
        />
      </div>
    </div>
  );

  // Lazy-loaded flat card component using Intersection Observer
  const LazyFlatCard = memo(
    ({
      item,
      ImageCarousel,
      onNavigate,
    }: {
      item: (typeof flats)[0];
      ImageCarousel: React.FC<{
        images: string[];
        altText: string;
      }>;
      onNavigate: (flatId: number, flatData: (typeof flats)[0]) => void;
    }) => {
      const [isVisible, setIsVisible] = useState(false);
      const cardRef = useRef<HTMLDivElement>(null);

      useEffect(() => {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observer.disconnect();
            }
          },
          {
            rootMargin: "200px", // Start loading 200px before entering viewport
            threshold: 0,
          },
        );

        if (cardRef.current) {
          observer.observe(cardRef.current);
        }

        return () => observer.disconnect();
      }, []);

      const roomData = item.room;
      const flatData = roomData.flat;
      const images = item.urls || [];
      const location = JSON.parse(flatData.location || "{}");
      const address = location.address || {};
      const cityName =
        address.city || address.town || address.village || address.suburb || "";
      const totalRent = Number(roomData.rent) + Number(roomData.utilities);
      const currency = location.currency || "CHF";

      const handleCardClick = useCallback(
        (e: React.MouseEvent) => {
          // Don't navigate if clicking on carousel buttons
          const target = e.target as HTMLElement;
          if (target.closest("button")) {
            return;
          }
          e.preventDefault();
          onNavigate(flatData.flat_id, item);
        },
        [flatData.flat_id, item, onNavigate],
      );

      return (
        <div
          ref={cardRef}
          className="block cursor-pointer"
          onClick={handleCardClick}
        >
          {/* Image Carousel */}
          <div className="relative aspect-square w-full overflow-hidden rounded-[24px] group">
            <div className="absolute inset-0 z-0" />
            {isVisible ? (
              <ImageCarousel images={images} altText={flatData.name} />
            ) : (
              <div
                className="w-full h-full"
                style={{ backgroundColor: containerNeutralLightMedium }}
              />
            )}
          </div>

          {/* Info */}
          <div className="pt-4 pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3
                  className="font-semibold text-base truncate"
                  style={{ color: textNeutralDefault }}
                >
                  {flatData.name}
                </h3>
                <p
                  className="text-sm mt-0.5"
                  style={{ color: textNeutralMuted }}
                >
                  {flatData.member_count} 👥, {roomData.size}m²
                  {cityName ? `, ${cityName}` : ""}
                </p>
              </div>
              <div
                className="px-3 py-1 rounded-full text-sm font-semibold flex-shrink-0"
                style={{
                  backgroundColor: containerTertiarySolidLow,
                  color: textTertiaryOnLow,
                }}
              >
                {currency} {totalRent}
              </div>
            </div>
          </div>
        </div>
      );
    },
    (prevProps, nextProps) =>
      prevProps.item.room.room_id === nextProps.item.room.room_id &&
      prevProps.onNavigate === nextProps.onNavigate,
  );

  // State for selected marker info window
  const [selectedMarker, setSelectedMarker] = useState<number | null>(null);

  // Map fullscreen state
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const toggleMapFullscreen = useCallback(() => {
    setIsMapFullscreen((prev) => !prev);
    setTimeout(() => {
      if (mapRef.current) {
        google.maps.event.trigger(mapRef.current, "resize");
      }
    }, 50);
  }, []);

  // Get flats with valid coordinates for the map
  const flatsWithCoords = useMemo(() => {
    return flats
      .filter((item) => {
        const location = JSON.parse(item.room.flat.location || "{}");
        return location.lat && location.lon;
      })
      .map((item) => {
        const location = JSON.parse(item.room.flat.location || "{}");
        return {
          ...item,
          lat: parseFloat(location.lat),
          lon: parseFloat(location.lon),
        };
      });
  }, [flats]);

  // Calculate map zoom based on delta
  const mapZoom = useMemo(() => {
    if (selectedLocation.delta >= 2) return 8;
    if (selectedLocation.delta >= 1) return 10;
    if (selectedLocation.delta >= 0.3) return 13;
    if (selectedLocation.delta >= 0.15) return 14;
    return 15;
  }, [selectedLocation.delta]);

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  // Map ref to control pan/zoom programmatically
  const mapRef = useRef<google.maps.Map | null>(null);
  const prevLocationRef = useRef(selectedLocation.name);
  const [mapReady, setMapReady] = useState(false);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      map.setCenter({ lat: selectedLocation.lat, lng: selectedLocation.lon });
      map.setZoom(mapZoom);
    },
    [selectedLocation.lat, selectedLocation.lon, mapZoom],
  );

  // Only pan/zoom when location changes (not on other re-renders)
  useEffect(() => {
    if (mapRef.current && prevLocationRef.current !== selectedLocation.name) {
      mapRef.current.panTo({
        lat: selectedLocation.lat,
        lng: selectedLocation.lon,
      });
      mapRef.current.setZoom(mapZoom);
      prevLocationRef.current = selectedLocation.name;
    }
  }, [selectedLocation, mapZoom]);

  // Handle map idle - refetch when pan or zoom changed meaningfully.
  const onMapIdle = useCallback(() => {
    if (!mapRef.current) return;
    if (!mapReady) setMapReady(true);

    const center = mapRef.current.getCenter();
    const bounds = mapRef.current.getBounds();
    if (!center || !bounds) return;

    const newLat = center.lat();
    const newLon = center.lng();

    // Calculate the new delta from bounds
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const newDelta = Math.max(ne.lat() - sw.lat(), ne.lng() - sw.lng()) / 2;

    // Cap threshold so very large deltas cannot suppress refetch forever.
    const distanceLat = Math.abs(newLat - mapRegion.lat);
    const distanceLon = Math.abs(newLon - mapRegion.lon);
    const threshold = Math.min(Math.max(mapRegion.delta * 0.3, 0.02), 0.6);
    const movedFarEnough = distanceLat > threshold || distanceLon > threshold;

    // Also refetch when zoom changes enough, even without large pan.
    const zoomChangedEnough =
      newDelta < mapRegion.delta * 0.75 || newDelta > mapRegion.delta * 1.25;

    if (movedFarEnough || zoomChangedEnough) {
      setMapRegion({
        lat: newLat,
        lon: newLon,
        delta: newDelta,
      });
    }
  }, [mapRegion, mapReady]);

  return (
    <div
      className="h-[calc(100dvh-2rem)] lg:h-screen flex font-gnarly-regular overflow-hidden"
      style={{
        backgroundColor: containerNeutralSolidSurface,
        color: textNeutralDefault,
      }}
    >
      {/* Filter Modal */}
      {isFilterOpen && (
        <FilterModal
          filters={{
            priceRange,
            flatmateCount,
            minRoomSize,
            genderFilter,
            stayType,
            appliedFilter,
          }}
          showGenderFilter={!!user}
          onApply={(newFilters) => {
            setPriceRange(newFilters.priceRange);
            setFlatmateCount(newFilters.flatmateCount);
            setMinRoomSize(newFilters.minRoomSize);
            setGenderFilter(newFilters.genderFilter);
            setStayType(newFilters.stayType);
            setAppliedFilter(newFilters.appliedFilter);
            setIsFilterOpen(false);
          }}
          onClose={() => setIsFilterOpen(false)}
        />
      )}

      {/* Main Content - Split View */}
      <main className="flex-1 flex overflow-hidden">
        {/* Map - Left Side (takes remaining space) - Hidden on mobile */}
        <div
          className={`hidden md:block flex-1 h-full relative py-4 pl-4 min-w-0 group/map ${isMapFullscreen ? "pr-4 lg:pl-0" : "pr-4 lg:pl-0"}`}
        >
          {/* Fullscreen toggle */}
          <button
            onClick={toggleMapFullscreen}
            aria-label={isMapFullscreen ? "Exit fullscreen map" : "Fullscreen map"}
            className="absolute top-8 right-8 z-10 w-10 h-10 rounded-xl flex items-center justify-center opacity-0 group-hover/map:opacity-100 transition-opacity duration-200 shadow-lg hover:scale-105 active:scale-95"
            style={{
              backgroundColor: containerNeutralSolidSurface,
              color: textNeutralDefault,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {isMapFullscreen ? (
              <Minimize2 size={18} />
            ) : (
              <Maximize2 size={18} />
            )}
          </button>
          {isLoaded ? (
            <div className="w-full h-full rounded-[32px] overflow-hidden [&_.gm-style-cc]:!hidden [&_.gmnoprint]:!hidden [&_a[href*='google.com/maps']]:!hidden [&_a[href*='terms']]:!hidden">
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "100%",
                }}
                onLoad={onMapLoad}
                onIdle={onMapIdle}
                options={{
                  disableDefaultUI: true,
                  zoomControl: false,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  scaleControl: false,
                  rotateControl: false,
                  panControl: false,
                  keyboardShortcuts: false,
                  styles: MAP_STYLES,
                  clickableIcons: false,
                }}
              >
                {mapReady &&
                  flatsWithCoords.map((item) => {
                    const roomData = item.room;
                    const location = JSON.parse(roomData.flat.location || "{}");
                    const totalRent =
                      Number(roomData.rent) + Number(roomData.utilities);
                    const currency = location.currency || "CHF";
                    const isSelected = selectedMarker === roomData.room_id;

                    return (
                      <OverlayViewF
                        key={roomData.room_id}
                        position={{ lat: item.lat, lng: item.lon }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          onClick={() =>
                            setSelectedMarker(
                              isSelected ? null : roomData.room_id,
                            )
                          }
                          className="cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 hover:scale-110"
                          style={{
                            backgroundColor: isSelected
                              ? textNeutralDefault
                              : containerNeutralSolidSurface,
                            color: isSelected
                              ? containerNeutralSolidSurface
                              : textNeutralDefault,
                            padding: "6px 10px",
                            borderRadius: "20px",
                            fontSize: "13px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          }}
                        >
                          {currency} {totalRent}
                        </div>
                      </OverlayViewF>
                    );
                  })}
                {/* Render preview card when marker is selected */}
                {mapReady &&
                  selectedMarker &&
                  (() => {
                    const selectedItem = flatsWithCoords.find(
                      (item) => item.room.room_id === selectedMarker,
                    );
                    if (!selectedItem) return null;
                    const roomData = selectedItem.room;
                    const flatData = roomData.flat;
                    const images = selectedItem.urls || [];
                    const location = JSON.parse(flatData.location || "{}");
                    const address = location.address || {};
                    const cityName =
                      address.city ||
                      address.town ||
                      address.village ||
                      address.suburb ||
                      "";
                    const totalRent =
                      Number(roomData.rent) + Number(roomData.utilities);
                    const currency = location.currency || "CHF";

                    // Calculate position relative to map bounds
                    const bounds = mapRef.current?.getBounds();
                    let verticalPosition: "above" | "below" = "above";
                    let horizontalPosition: "left" | "center" | "right" =
                      "center";

                    if (bounds) {
                      const ne = bounds.getNorthEast();
                      const sw = bounds.getSouthWest();
                      const latRange = ne.lat() - sw.lat();
                      const lngRange = ne.lng() - sw.lng();

                      // Check vertical position (if in top 40%, show below)
                      const latPercent =
                        (selectedItem.lat - sw.lat()) / latRange;
                      if (latPercent > 0.6) {
                        verticalPosition = "below";
                      }

                      // Check horizontal position (left/right 35%)
                      const lngPercent =
                        (selectedItem.lon - sw.lng()) / lngRange;
                      if (lngPercent < 0.35) {
                        horizontalPosition = "left";
                      } else if (lngPercent > 0.65) {
                        horizontalPosition = "right";
                      }
                    }

                    // Build transform classes based on position
                    const verticalTransform =
                      verticalPosition === "above"
                        ? "-translate-y-full -mt-2"
                        : "translate-y-4";
                    const horizontalTransform =
                      horizontalPosition === "left"
                        ? "translate-x-0"
                        : horizontalPosition === "right"
                          ? "-translate-x-full"
                          : "-translate-x-1/2";

                    return (
                      <OverlayViewF
                        position={{
                          lat: selectedItem.lat,
                          lng: selectedItem.lon,
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          className={`relative transform ${horizontalTransform} ${verticalTransform}`}
                          style={{
                            width: 340,
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            onClick={() =>
                              handleFlatNavigate(flatData.flat_id, selectedItem)
                            }
                            className="block rounded-[24px] overflow-hidden shadow-xl cursor-pointer"
                            style={{
                              backgroundColor: containerNeutralSolidSurface,
                            }}
                          >
                            {/* Image Carousel */}
                            <div className="relative aspect-[4/3] w-full overflow-hidden group">
                              <ImageCarousel
                                images={images}
                                altText={flatData.name}
                              />
                            </div>

                            {/* Info */}
                            <div className="p-3 hover:bg-black/5 transition-colors">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <h3
                                    className="font-semibold text-sm truncate"
                                    style={{ color: textNeutralDefault }}
                                  >
                                    {flatData.name}
                                  </h3>
                                  <p
                                    className="text-xs mt-0.5"
                                    style={{ color: textNeutralMuted }}
                                  >
                                    {flatData.member_count} 👥, {roomData.size}
                                    m²
                                    {cityName ? `, ${cityName}` : ""}
                                  </p>
                                </div>
                                <div
                                  className="px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                                  style={{
                                    backgroundColor: containerTertiarySolidLow,
                                    color: textTertiaryOnLow,
                                  }}
                                >
                                  {currency} {totalRent}
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Close button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedMarker(null);
                            }}
                            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                            style={{
                              backgroundColor: "rgba(0,0,0,0.5)",
                              color: "white",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      </OverlayViewF>
                    );
                  })()}
              </GoogleMap>
            </div>
          ) : (
            <div
              className="w-full h-full rounded-[32px] flex items-center justify-center"
              style={{ backgroundColor: containerNeutralLightMedium }}
            >
              <p style={{ color: textNeutralMuted }}>Loading map...</p>
            </div>
          )}
        </div>

        {/* List - Right Side (fixed 50vw on desktop) - Full width on mobile */}
        <div
          className={`w-full md:w-[50vw] h-full flex flex-col overflow-hidden flex-shrink-0 ${isMapFullscreen ? "md:hidden" : ""}`}
        >
          {/* Single scrollable container - list scrolls behind sticky search bar */}
          <div
            ref={scrollContainerRef}
            onScroll={debouncedScrollHandler}
            className="flex-1 overflow-y-auto px-4 md:pl-4 md:pr-8"
          >
            {/* Search Bar and Filters - sticky at top */}
            <div className="sticky top-0 z-20 pt-4 pb-3">
              {/* Solid white block to hide content above and around the search bar */}
              <div
                className="absolute -top-4 left-0 right-0 bottom-1/2 z-0"
                style={{ backgroundColor: containerNeutralSolidSurface }}
              />
              <div className="relative rounded-full">
                {/* White backing layer for contrast */}
                <div
                  className="absolute inset-0 rounded-full backdrop-blur-md"
                  style={{ backgroundColor: "rgba(255,255,255,0.85)" }}
                />
                <div
                  className="relative flex items-center gap-1.5 p-1.5 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: containerNeutralLightLowest,
                    boxShadow:
                      "0 1px 2px rgba(0,0,0,0.04), 0 3px 10px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Location Search Bar */}
                  <div className="flex-1 relative" ref={searchContainerRef}>
                    <div className="relative flex items-center">
                      <Search
                        size={18}
                        className="absolute left-3.5 pointer-events-none"
                        style={{ color: textNeutralDefault }}
                      />
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) =>
                          handleSearchInputChange(e.target.value)
                        }
                        onFocus={() =>
                          suggestions.length > 0 && setShowSuggestions(true)
                        }
                        placeholder={
                          t("search_location") || "Search location..."
                        }
                        className="w-full pl-10 pr-9 py-2.5 rounded-xl text-base font-medium outline-none bg-transparent"
                        style={{
                          color: textNeutralDefault,
                        }}
                      />
                      {searchInput && (
                        <button
                          onClick={() => {
                            setSearchInput("");
                            setSuggestions([]);
                            setShowSuggestions(false);
                            setSelectedLocation(DEFAULT_LOCATION);
                          }}
                          className="absolute right-2.5 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110 active:scale-90"
                          style={{
                            backgroundColor: containerNeutralLightMedium,
                          }}
                        >
                          <X size={11} style={{ color: textNeutralMuted }} />
                        </button>
                      )}
                    </div>

                    {/* Loading indicator */}
                    {isSearching && (
                      <div
                        className="absolute left-0 right-0 top-full mt-2 p-3 rounded-2xl shadow-lg z-50 animate-[scaleIn_150ms_cubic-bezier(0.16,1,0.3,1)]"
                        style={{
                          backgroundColor: containerNeutralSolidSurface,
                          border: `1px solid ${containerNeutralLightLowest}`,
                        }}
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin flex-shrink-0"
                            style={{
                              borderColor: `${accentColor} transparent ${accentColor} ${accentColor}`,
                            }}
                          />
                          <span
                            className="text-sm leading-none"
                            style={{ color: textNeutralMuted }}
                          >
                            {t("loading") || "Searching..."}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Suggestions dropdown */}
                    {showSuggestions &&
                      suggestions.length > 0 &&
                      !isSearching && (
                        <div
                          className="absolute left-0 right-0 top-full mt-2 rounded-2xl shadow-lg z-50 overflow-hidden animate-[scaleIn_150ms_cubic-bezier(0.16,1,0.3,1)]"
                          style={{
                            backgroundColor: containerNeutralSolidSurface,
                            border: `1px solid ${containerNeutralLightLowest}`,
                          }}
                        >
                          {suggestions.map((suggestion, index) => (
                            <button
                              key={suggestion.place_id || index}
                              onClick={() => handleSelectSuggestion(suggestion)}
                              className="w-full px-3 py-2.5 text-left transition-all duration-150 flex items-center gap-3 group/suggestion animate-[fadeIn_200ms_ease-out_both]"
                              style={{
                                borderBottom:
                                  index < suggestions.length - 1
                                    ? `1px solid ${containerNeutralLightLowest}`
                                    : "none",
                                animationDelay: `${index * 40}ms`,
                              }}
                            >
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-150 group-hover/suggestion:scale-110"
                                style={{
                                  backgroundColor: containerNeutralLightLowest,
                                }}
                              >
                                <MapPin
                                  size={14}
                                  className="transition-colors duration-150"
                                  style={{ color: textNeutralMuted }}
                                />
                              </div>
                              <span
                                className="text-sm line-clamp-2 transition-colors duration-150"
                                style={{ color: textNeutralDefault }}
                              >
                                {suggestion.display_name}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Filter Button */}
                  <button
                    onClick={() => setIsFilterOpen(true)}
                    className="flex items-center gap-1.5 pl-3.5 pr-3.5 py-2.5 rounded-full text-sm font-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] relative flex-shrink-0"
                    style={{
                      backgroundColor:
                        activeFilterCount > 0 ? accentColor : "white",
                      color:
                        activeFilterCount > 0
                          ? textNeutralOnPrimary
                          : textNeutralDefault,
                      boxShadow:
                        activeFilterCount > 0
                          ? `0 2px 8px ${accentColor}35`
                          : "none",
                    }}
                  >
                    <SlidersHorizontal size={16} />
                    <span className="hidden sm:inline">
                      {t("filters") || "Filters"}
                    </span>
                    {activeFilterCount > 0 && (
                      <span
                        className="min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full leading-none"
                        style={{
                          backgroundColor: textNeutralOnPrimary,
                          color: accentColor,
                        }}
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* List content - scrolls behind the sticky search bar */}
            <div className="pt-4 pb-6">
              {flatsQuery.isLoading && flats.length === 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 min-[2400px]:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : flats.length === 0 ? (
                <div className="text-center py-16">
                  <p
                    className="text-lg mb-2"
                    style={{ color: textNeutralDefault }}
                  >
                    No flats found
                  </p>
                  <p className="text-sm" style={{ color: textNeutralMuted }}>
                    Try adjusting your filters or search in a different area
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 min-[2400px]:grid-cols-3 gap-6">
                  {flats.map((item) => (
                    <LazyFlatCard
                      key={item.room.room_id}
                      item={item}
                      ImageCarousel={ImageCarousel}
                      onNavigate={handleFlatNavigate}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default BrowseFlats;
